import React from 'react';
import { Checkbox, RadioButton, Select } from '@gravity-ui/uikit';
import isObjectLike from 'lodash/isObjectLike';
import some from 'lodash/some';
import { TogglerCard } from '../../components';
import { block, objectKeys } from '../../utils';
import './useOneOf.css';
const b = block('use-oneof');
const MAX_TAB_TITLE_LENGTH = 20;
export const useOneOf = ({ props, onTogglerChange }) => {
    var _a, _b;
    const { name, input, spec, Layout } = props;
    const specProperties = React.useMemo(() => (isObjectLike(spec.properties) ? spec.properties : {}), [spec.properties]);
    const specBooleanMap = React.useMemo(() => { var _a; return (_a = spec.viewSpec.oneOfParams) === null || _a === void 0 ? void 0 : _a.booleanMap; }, [(_a = spec.viewSpec.oneOfParams) === null || _a === void 0 ? void 0 : _a.booleanMap]);
    const [oneOfValue, setOneOfValue] = React.useState(() => {
        let valueKeys;
        if (isObjectLike(input.value)) {
            const keys = Object.keys(input.value);
            if (keys.length) {
                valueKeys = keys;
            }
        }
        return (valueKeys || spec.viewSpec.order || Object.keys(specProperties))[0];
    });
    const onOneOfChange = React.useCallback(([newValue]) => {
        if (newValue !== oneOfValue) {
            input.onChange({});
            setOneOfValue(newValue);
            onTogglerChange === null || onTogglerChange === void 0 ? void 0 : onTogglerChange(newValue);
        }
    }, [setOneOfValue, input.onChange, oneOfValue]);
    const onCheckboxChange = React.useCallback((checked) => {
        if (specBooleanMap) {
            const value = String(checked);
            const newValue = specBooleanMap[value];
            onOneOfChange([newValue]);
        }
    }, [onOneOfChange, specBooleanMap]);
    const checkboxValue = React.useMemo(() => {
        if (specBooleanMap) {
            const keyBooleanMap = objectKeys(specBooleanMap).find((key) => specBooleanMap[key] === oneOfValue);
            return keyBooleanMap === 'true';
        }
        return undefined;
    }, [oneOfValue, specBooleanMap]);
    const options = React.useMemo(() => (spec.viewSpec.order || Object.keys(specProperties)).map((value) => {
        var _a, _b;
        const title = ((_a = spec.description) === null || _a === void 0 ? void 0 : _a[value]) ||
            ((_b = specProperties[value]) === null || _b === void 0 ? void 0 : _b.viewSpec.layoutTitle) ||
            value ||
            '';
        return {
            value,
            title,
            content: title,
        };
    }), [spec.description, spec.viewSpec.order, specProperties]);
    const togglerType = React.useMemo(() => {
        var _a, _b, _c, _d;
        if (((_a = spec.viewSpec.oneOfParams) === null || _a === void 0 ? void 0 : _a.toggler) === 'card' && options.length < 3) {
            return 'card';
        }
        if (((_b = spec.viewSpec.oneOfParams) === null || _b === void 0 ? void 0 : _b.toggler) !== 'radio' &&
            (((_c = spec.viewSpec.oneOfParams) === null || _c === void 0 ? void 0 : _c.toggler) === 'select' ||
                options.length > 3 ||
                some(options, ({ title }) => title.length > MAX_TAB_TITLE_LENGTH))) {
            return 'select';
        }
        if (((_d = spec.viewSpec.oneOfParams) === null || _d === void 0 ? void 0 : _d.toggler) === 'checkbox' &&
            options.length === 2 &&
            specBooleanMap) {
            return 'checkbox';
        }
        return 'radio';
    }, [options, (_b = spec.viewSpec.oneOfParams) === null || _b === void 0 ? void 0 : _b.toggler, specBooleanMap]);
    const togglerInput = React.useMemo(() => {
        if (togglerType === 'card') {
            return (React.createElement("div", { className: b('card') }, options.map(({ value }) => {
                var _a, _b, _c;
                const onClick = () => {
                    onOneOfChange([value]);
                };
                return (React.createElement(TogglerCard, { title: ((_a = specProperties[value]) === null || _a === void 0 ? void 0 : _a.viewSpec.layoutTitle) || '', disabled: spec.viewSpec.disabled, text: ((_b = spec.description) === null || _b === void 0 ? void 0 : _b[value]) || '', description: (_c = specProperties[value]) === null || _c === void 0 ? void 0 : _c.viewSpec.layoutDescription, onClick: onClick, selected: oneOfValue === value, key: value }));
            })));
        }
        if (togglerType === 'select') {
            return (React.createElement(Select, { width: "max", value: [oneOfValue], onUpdate: onOneOfChange, options: options, disabled: spec.viewSpec.disabled, filterable: options.length > 7, qa: name }));
        }
        if (togglerType === 'checkbox') {
            return (React.createElement("div", { className: b('checkbox') },
                React.createElement(Checkbox, { checked: checkboxValue, onUpdate: onCheckboxChange, disabled: spec.viewSpec.disabled, qa: name })));
        }
        return (React.createElement(RadioButton, { value: oneOfValue, onChange: (event) => onOneOfChange([event.target.value]), disabled: spec.viewSpec.disabled, qa: name }, options.map((option) => (React.createElement(RadioButton.Option, { key: option.value, value: option.value }, option.title)))));
    }, [
        togglerType,
        oneOfValue,
        spec.viewSpec.disabled,
        spec.description,
        name,
        options,
        onOneOfChange,
        specProperties,
        onCheckboxChange,
        checkboxValue,
    ]);
    const toggler = React.useMemo(() => {
        if (Layout) {
            return (React.createElement("div", { className: b('toggler', {
                    radio: togglerType === 'radio',
                    card: togglerType === 'card',
                }) },
                React.createElement(Layout, Object.assign({}, props), togglerInput)));
        }
        return React.createElement("div", null, togglerInput);
    }, [Layout, togglerInput, togglerType, props]);
    return { oneOfValue, specProperties, toggler, togglerInput };
};
